import { BadgeProps } from '@fluentui/react-components';
import { TYPE_OPTIONS, DECISION_OPTIONS, EXCLUSION_REASON_OPTIONS, REVIEWING_STAGE_OPTIONS } from './constants';
import { blobHref, proxyUrl } from '@shared/utils';
import { EAssessmentDecision, TLiterature, TLiteratureAssessment, TLiteratureContent } from './types';

export const getTypeLabel = (val: string) => TYPE_OPTIONS.find((o) => o.value === val)?.label ?? val;

export const getDecisionLabel = (val: string) => DECISION_OPTIONS.find((o) => o.value === val)?.label ?? val;

export const getReviewingStageLabel = (val: string) =>
  REVIEWING_STAGE_OPTIONS.find((o) => o.value === val)?.label ?? val;

export const getExclusionReasonLabel = (val: string) =>
  EXCLUSION_REASON_OPTIONS.find((o) => o.value === val)?.label ?? val;

export const getLitContainerName = (organizationId: string) => `lit-${organizationId}`;

// Returns a backend-proxied inline URL so the browser opens the PDF in a new tab
// instead of downloading it. Raw Azure blob URLs trigger a download by default.
export const getLiteratureAttachmentUrl = (
  organizationId: string,
  blobName: string,
  token: string,
  title: string
): string => {
  const rawHref = blobHref(getLitContainerName(organizationId), blobName, token);
  return proxyUrl(rawHref, `${title || 'literature'}.pdf`, 'application/pdf', true);
};

// Returns undefined only for malformed records; schema guarantees at least a draft or one version.
export const getLiteratureDisplayContent = (item: TLiterature): TLiteratureContent | undefined =>
  item.draft?.content ?? item.versions[0]?.content;

export const getLiteratureTitle = (item: TLiterature): string =>
  getLiteratureDisplayContent(item)?.title ?? '(untitled)';

const MAX_BYLINE_AUTHORS = 4;
export const getLiteratureByline = (content: TLiteratureContent): string => {
  const authors = content.authors?.length
    ? content.authors.length > MAX_BYLINE_AUTHORS
      ? `${content.authors.slice(0, MAX_BYLINE_AUTHORS).join(', ')}, et al`
      : content.authors.join(', ')
    : '';
  const secondary = content.containerTitle || content.publisher || '';
  return [authors, content.publicationYear, secondary].filter((v) => v != null && v !== '').join(' · ');
};

// Harvard-style short ref for sidebar rows: `Surname, Year` / `Surname & Co, Year` / `Surname et al., Year`.
const getAuthorSurname = (name: string): string => name.trim().split(/\s+/).at(-1) ?? name;
export const getLiteratureShortRef = (content: TLiteratureContent): string => {
  const authors = content.authors ?? [];
  const year = content.publicationYear;
  const authorPart =
    authors.length === 0
      ? ''
      : authors.length === 1
        ? getAuthorSurname(authors[0])
        : authors.length === 2
          ? `${getAuthorSurname(authors[0])} & ${getAuthorSurname(authors[1])}`
          : `${getAuthorSurname(authors[0])} et al.`;
  return [authorPart, year].filter((v) => v != null && v !== '').join(', ');
};

export const getAssessmentForProduct = (item: TLiterature, productId?: string): TLiteratureAssessment | undefined => {
  if (!productId) return undefined;
  return item.assessments.find((a) => !a.archived && a.products.includes(productId));
};

// Derived from Fluent's own BadgeProps['color'] so a future Fluent rename (e.g. 'danger' → 'critical') surfaces at compile time.
export const getDecisionBadgeColor = (decision: EAssessmentDecision): BadgeProps['color'] => {
  switch (decision) {
    case EAssessmentDecision.INCLUDED:
      return 'brand';
    case EAssessmentDecision.EXCLUDED:
      return 'danger';
    case EAssessmentDecision.PENDING:
      return 'warning';
    default:
      return 'informative';
  }
};
